/** Opaque referral transport. No advertising identifiers belong in this module. */
export const REFERRAL_COOKIE = "wiswiz_student_referral_v1";
export const PARENT_REFERRAL_URL = "https://wiswiz.nl/voor-ouders?source=student_referral";
const CODE = /^[A-Za-z0-9_-]{16}$/;
type Receipt = { value: string; expires: number };
declare global {
  interface Window { wiswizReferralArrival?: string; wiswizReferralUnsafe?: boolean; }
}
let current: Receipt | null = null;
let resolving: Promise<void> | null = null;
let generation = 0;

// Runs before measurement preload hints and before either pixel base tag.
// The receipt never enters the URL or the advertising event envelope.
export const REFERRAL_ARRIVAL_SCRIPT = `(function(){try{var u=new URL(location.href);if(!/^\\/voor-ouders(?:\\/|$)/.test(u.pathname)||!u.searchParams.has('sharecode'))return;window.wiswizReferralUnsafe=true;var c=u.searchParams.get('sharecode');u.searchParams.delete('sharecode');u.searchParams.set('source','student_referral');var m=document.createElement('meta');m.name='referrer';m.content='no-referrer';document.head.appendChild(m);history.replaceState(history.state,'',u.pathname+u.search+u.hash);window.wiswizReferralUnsafe=false;if(/^[A-Za-z0-9_-]{16}$/.test(c||''))window.wiswizReferralArrival=c}catch(e){}})()`;

function receipt(value: unknown): Receipt | null {
  if (typeof value !== "string" || value.length > 1024) return null;
  try {
    const claims = JSON.parse(atob(value.split(".")[1].replace(/-/g, "+").replace(/_/g, "/")));
    if (claims.aud !== "student-parent-referral-v1" || claims.flow !== "student_parent_offer" ||
        !Number.isSafeInteger(claims.exp) || claims.exp * 1000 <= Date.now()) return null;
    // Only the backend verifies the signature and accepts a payment binding.
    return { value, expires: claims.exp * 1000 };
  } catch { return null; }
}

function clearReceipt() {
  current = null;
  try { document.cookie = `${REFERRAL_COOKIE}=; path=/; max-age=0; samesite=lax`; } catch { /* Optional storage. */ }
}

function saveReceipt(next: Receipt) {
  current = next;
  try {
    // A successful resolve is available only behind the backend's approved-basis
    // gate. Expiring first-party storage never makes an unknown ad choice granted.
    document.cookie = `${REFERRAL_COOKIE}=${encodeURIComponent(next.value)}; path=/; expires=${new Date(next.expires).toUTCString()}; samesite=lax${location.protocol === "https:" ? "; secure" : ""}`;
  } catch { /* Memory still carries the receipt through same-document navigation. */ }
}

export function readReferralReceipt(): string | undefined {
  if (typeof window === "undefined") return undefined;
  if (current && current.expires <= Date.now()) clearReceipt();
  if (current) return current.value;
  try {
    const raw = document.cookie.split("; ").find(part => part.startsWith(`${REFERRAL_COOKIE}=`));
    if (!raw) return undefined;
    current = receipt(decodeURIComponent(raw.slice(REFERRAL_COOKIE.length + 1)));
    if (!current) clearReceipt();
    return current?.value;
  } catch { return undefined; }
}

export function captureParentReferral(): Promise<void> {
  if (typeof window === "undefined") return Promise.resolve();
  // The streamed script normally strips it. This also covers client navigation.
  const url = new URL(window.location.href);
  const code = window.wiswizReferralArrival ?? url.searchParams.get("sharecode");
  delete window.wiswizReferralArrival;
  if (url.searchParams.has("sharecode")) {
    url.searchParams.delete("sharecode");
    url.searchParams.set("source", "student_referral");
    try { history.replaceState(history.state, "", url.pathname + url.search + url.hash); } catch { return Promise.resolve(); }
  }
  if (!code || !CODE.test(code)) { readReferralReceipt(); return resolving ?? Promise.resolve(); }
  const ownGeneration = ++generation;
  clearReceipt();
  resolving = fetch("/api/tiktok-share-attribution/resolve", {
    method: "POST", headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ share_code: code }), referrerPolicy: "no-referrer"
  }).then(async response => {
    if (!response.ok) return;
    const next = receipt((await response.json()).referral_receipt);
    if (next && generation === ownGeneration) saveReceipt(next);
  }).catch(() => {}).finally(() => { if (generation === ownGeneration) resolving = null; });
  return resolving;
}

/** The consent integration calls this on withdrawal, even with collection off. */
export function withdrawReferralPermission(): void {
  const previous = readReferralReceipt();
  generation += 1;
  delete window.wiswizReferralArrival;
  clearReceipt();
  if (!previous) return;
  try {
    void fetch("/api/tiktok-share-attribution/revoke", {
      method: "POST", headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ referral_receipt: previous }), keepalive: true,
      referrerPolicy: "no-referrer"
    }).catch(() => {});
  } catch { /* Withdrawal clears local context even during an outage. */ }
}

type PreparedLink = { url: string; expiresAt: number };
let prepared: Promise<PreparedLink | null> | null = null;
let preparedFor = "";
export function prepareStudentReferral(): Promise<PreparedLink | null> {
  if (prepared && preparedFor === location.search) return prepared;
  preparedFor = location.search;
  try {
    const query = new URLSearchParams(location.search);
    const campaign: Record<string, string> = {};
    for (const key of ["utm_source", "utm_medium", "utm_campaign", "utm_content"]) {
      const value = query.get(key);
      if (value && /^[\w .:/-]{1,128}$/.test(value)) campaign[key] = value;
    }
    prepared = fetch("/api/tiktok-share-attribution", {
      method: "POST", credentials: "include", headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ source_flow: "student_parent_offer", student_session_id: crypto.randomUUID(),
        consent_state: "unknown", ...campaign }), referrerPolicy: "no-referrer"
    }).then(async response => {
      if (!response.ok) return null;
      const data = await response.json();
      const expiresAt = Date.parse(data.expires_at);
      return CODE.test(data.share_code ?? "") && expiresAt > Date.now()
        ? { url: `${PARENT_REFERRAL_URL}&sharecode=${encodeURIComponent(data.share_code)}`, expiresAt } : null;
    }).catch(() => null);
  } catch { prepared = Promise.resolve(null); }
  return prepared;
}
